import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import {
  useBlocker,
  type BlockerFunction,
  type Location,
} from 'react-router-dom';

import { getDashboardEditorThreadId } from '../services/dashboardRouteTo';

export type AgentRunSurface = 'page' | 'side-panel';

export interface AgentRunThreadIdentity {
  agentName: string;
  scopeKey: string;
  threadId: string;
}

interface AgentRunNavigationRegistration {
  agentName: string;
  scopeKey: string;
  threadId: string | null;
  surface: AgentRunSurface;
  running: boolean;
  continueInBackground: () => void;
  openThread: (threadId: string) => void;
}

interface AgentRunNavigationContextValue {
  register: (
    id: string,
    registration: AgentRunNavigationRegistration,
  ) => () => void;
  guardAction: (
    surface: AgentRunSurface,
    proceed: () => void,
    destinationThreadId?: string,
  ) => void;
  isThreadVisible: (thread: AgentRunThreadIdentity) => boolean;
}

interface AgentRunNavigationRegistrationOptions extends AgentRunNavigationRegistration {
  active: boolean;
}

const AgentRunNavigationContext =
  createContext<AgentRunNavigationContextValue | null>(null);

const getRegistrationsForSurface = (
  registrations: Map<string, AgentRunNavigationRegistration>,
  surface: AgentRunSurface,
) =>
  [...registrations.values()].filter(
    (registration) => registration.surface === surface,
  );

const getRunningRegistrationsForSurface = (
  registrations: Map<string, AgentRunNavigationRegistration>,
  surface: AgentRunSurface,
) =>
  getRegistrationsForSurface(registrations, surface).filter(
    (registration) => registration.running,
  );

const registrationMatchesThreadScope = (
  registration: AgentRunNavigationRegistration,
  thread: AgentRunThreadIdentity,
) =>
  registration.agentName === thread.agentName &&
  registration.scopeKey === thread.scopeKey;

const registrationMatchesThread = (
  registration: AgentRunNavigationRegistration,
  thread: AgentRunThreadIdentity,
) =>
  registration.threadId === thread.threadId &&
  registrationMatchesThreadScope(registration, thread);

const getSuperagentRoute = (location: Location) => {
  const match = location.pathname.match(
    /^(\/workspace\/[^/]+\/superagent)(?:\/([^/]+))?\/?$/,
  );

  if (!match) {
    return null;
  }

  return {
    root: match[1],
    threadId: match[2] ?? null,
  };
};

export function AgentRunNavigationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const registrationsRef = useRef(
    new Map<string, AgentRunNavigationRegistration>(),
  );
  const preservedPageThreadIdRef = useRef<string | undefined>(undefined);

  const register = useCallback(
    (id: string, registration: AgentRunNavigationRegistration) => {
      registrationsRef.current.set(id, registration);

      return () => {
        registrationsRef.current.delete(id);
      };
    },
    [],
  );

  const isThreadVisible = useCallback(
    (thread: AgentRunThreadIdentity) =>
      [...registrationsRef.current.values()].some((registration) =>
        registrationMatchesThread(registration, thread),
      ),
    [],
  );

  const continueSurfaceRunsInBackground = useCallback(
    (surface: AgentRunSurface, preservedThreadId?: string) => {
      const registrations = getRunningRegistrationsForSurface(
        registrationsRef.current,
        surface,
      ).filter(({ threadId }) => threadId !== preservedThreadId);

      for (const { continueInBackground } of registrations) {
        continueInBackground();
      }
    },
    [],
  );

  const guardAction = useCallback(
    (
      surface: AgentRunSurface,
      proceed: () => void,
      destinationThreadId?: string,
    ) => {
      const runningRegistrations = getRunningRegistrationsForSurface(
        registrationsRef.current,
        surface,
      );
      if (runningRegistrations.length > 0) {
        continueSurfaceRunsInBackground(surface, destinationThreadId);
      }

      proceed();
    },
    [continueSurfaceRunsInBackground],
  );

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      preservedPageThreadIdRef.current = undefined;
      const pageRegistrations = getRunningRegistrationsForSurface(
        registrationsRef.current,
        'page',
      );

      if (pageRegistrations.length === 0) {
        return false;
      }

      if (
        currentLocation.pathname === nextLocation.pathname &&
        currentLocation.search === nextLocation.search
      ) {
        return false;
      }

      const currentSuperagentRoute = getSuperagentRoute(currentLocation);
      const nextSuperagentRoute = getSuperagentRoute(nextLocation);

      if (nextSuperagentRoute?.threadId === null) {
        return false;
      }

      const editorThreadId = getDashboardEditorThreadId(nextLocation.state);
      const nextSuperagentThreadId =
        currentSuperagentRoute &&
        nextSuperagentRoute &&
        currentSuperagentRoute.root === nextSuperagentRoute.root &&
        nextSuperagentRoute.threadId
          ? nextSuperagentRoute.threadId
          : undefined;
      const destinationThreadId = editorThreadId ?? nextSuperagentThreadId;
      const hasDestinationRun = pageRegistrations.some(
        ({ agentName, threadId }) =>
          threadId === destinationThreadId && agentName === 'superAgent',
      );

      if (hasDestinationRun) {
        const hasUnrelatedRun = pageRegistrations.some(
          ({ agentName, threadId }) =>
            threadId !== destinationThreadId || agentName !== 'superAgent',
        );
        if (!hasUnrelatedRun) {
          return false;
        }

        preservedPageThreadIdRef.current = destinationThreadId;
      }

      return true;
    },
    [],
  );

  const blocker = useBlocker(shouldBlock);
  const contextValue = useMemo(
    () => ({
      guardAction,
      isThreadVisible,
      register,
    }),
    [guardAction, isThreadVisible, register],
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      return;
    }

    continueSurfaceRunsInBackground('page', preservedPageThreadIdRef.current);
    preservedPageThreadIdRef.current = undefined;
    blocker.proceed?.();
  }, [blocker, continueSurfaceRunsInBackground]);

  return (
    <AgentRunNavigationContext.Provider value={contextValue}>
      {children}
    </AgentRunNavigationContext.Provider>
  );
}

export function useAgentRunActionGuard(surface: AgentRunSurface) {
  const context = useContext(AgentRunNavigationContext);

  return useCallback(
    (proceed: () => void, destinationThreadId?: string) => {
      if (!context) {
        proceed();
        return;
      }

      context.guardAction(surface, proceed, destinationThreadId);
    },
    [context, surface],
  );
}

export function useAgentRunNavigationRegistration({
  active,
  running,
  ...registration
}: AgentRunNavigationRegistrationOptions) {
  const context = useContext(AgentRunNavigationContext);
  const id = useId();
  const registrationRef = useRef({ ...registration, running });
  registrationRef.current = { ...registration, running };

  useEffect(() => {
    if (!context || !active) {
      return;
    }

    return context.register(id, {
      get agentName() {
        return registrationRef.current.agentName;
      },
      get scopeKey() {
        return registrationRef.current.scopeKey;
      },
      get threadId() {
        return registrationRef.current.threadId;
      },
      get surface() {
        return registrationRef.current.surface;
      },
      get running() {
        return registrationRef.current.running;
      },
      continueInBackground: () =>
        registrationRef.current.continueInBackground(),
      openThread: (threadId) => registrationRef.current.openThread(threadId),
    });
  }, [active, context, id]);
}

export function useAgentRunNotificationNavigation() {
  const context = useContext(AgentRunNavigationContext);

  return {
    isThreadVisible: context?.isThreadVisible,
  };
}
